import { selectIntpWebsite, selectStpRates } from '@va/aaas/shared/selectors';
import { AaasBillingMode, AaasPackage } from '@va/aaas/shared/types';
import { is3ASUnlimitedFreeTrialPlan, PlanNames, websiteFlagKeys } from '@va/constants';
import { updateWebsiteFrontendFlags } from '@va/dashboard/api-client/Settings';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { getLastSeenFreeTrialModal } from '@va/dashboard/selectors/core';
import { useLocalizationContext } from '@va/localization';
import { addNumberSeparator } from '@va/util/helpers';
import dayjs from 'dayjs';
import { clamp } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AaasPricingPlanSuggestionModal } from '../PricingPlanSuggestionModal';

const daysLeftToShowModal = [15, 5, 2, 1, 0];

export const AaasFreeTrialEndingModal = () => {
  const lastSeenModal = useSelector(getLastSeenFreeTrialModal);
  const { stpLimit } = useSelector(selectStpRates);
  const { packageName: currentPlanName, billingMode } = useSelector(selectIntpWebsite);
  const { expiresAt } = useSelector(selectIntpWebsite);

  const daysLeft = useMemo(() => {
    if (!(stpLimit === -1 && is3ASUnlimitedFreeTrialPlan(currentPlanName))) return null;
    return dayjs(expiresAt).diff(dayjs(), 'day');
  }, [currentPlanName, expiresAt, stpLimit]);

  const isOpen = useMemo(
    () => daysLeftToShowModal.some((day) => day === daysLeft && lastSeenModal !== day),
    [daysLeft, lastSeenModal],
  );

  // This is a quick fix for VISA-14457
  // In the current implementation the modal will always asume the free trial period is 30 days, and there is a free plan available, but this is no longer the case
  // The modal should be updated to integrate with the new levels of customisation
  if (billingMode === AaasBillingMode.PlatformManaged) {
    return null;
  }

  if (!isOpen || daysLeft === null) return null;

  return <AaasFreeTrialEndingModalContent daysLeft={daysLeft} />;
};

const AaasFreeTrialEndingModalContent = ({ daysLeft }: { daysLeft: number }) => {
  const websiteId = useSelector(getInstanceId);
  const { locale, translate } = useLocalizationContext();
  const { stpConsumed } = useSelector(selectStpRates);

  const daysPassed = useMemo(() => clamp(30 - daysLeft, 1, 30), [daysLeft]);

  const handleClose = useCallback(async () => {
    await updateWebsiteFrontendFlags(websiteId, { [websiteFlagKeys.LAST_SEEN_FREE_TRIAL_MODAL]: daysLeft });
  }, [daysLeft, websiteId]);

  const title = useMemo(() => {
    if (daysLeft <= 0) return translate('modals.freeTrialEnding.title.ended');
    if (daysLeft === 1) return translate('modals.freeTrialEnding.title.oneDay');
    return translate('modals.freeTrialEnding.title.daysCount', { count: daysLeft });
  }, [daysLeft, translate]);

  const getRecommendedPlan = useCallback(
    (plans: AaasPackage[]) => {
      if (stpConsumed === undefined || daysLeft === null) return null;
      const estimatedStpsPerMonth = stpConsumed * (30 / daysPassed);
      const eligiblePlans = plans.filter((plan) => plan.name !== PlanNames.Free);

      return (
        eligiblePlans.find((plan) => estimatedStpsPerMonth <= plan.touchpoints) ||
        eligiblePlans.find((plan) => plan.name === PlanNames.Basic)
      );
    },
    [daysLeft, daysPassed, stpConsumed],
  );

  const subtitle = useMemo(
    () => (
      <>
        {translate('modals.freeTrialEnding.pastDaysStp', { count: daysPassed })}&nbsp;
        <span className='text-primary font-semibold'>
          {translate('modals.freeTrialEnding.stpConsumed', {
            count: addNumberSeparator(stpConsumed, locale),
          })}
        </span>
      </>
    ),
    [translate, daysPassed, stpConsumed, locale],
  );

  return (
    <AaasPricingPlanSuggestionModal
      getRecommendedPlan={getRecommendedPlan}
      title={title}
      hint={translate('modals.freeTrialEnding.touchpointsHintText')}
      subtitle={subtitle}
      onClose={handleClose}
      isLoading={false}
    />
  );
};
